<template>
  <div class="d-flex my-4">
    <!-- Back button -->
    <v-icon color="secondary" @click="handleBack" size="36">
      mdi-keyboard-backspace
    </v-icon>
    <h2 class="flex-grow-1 ml-2">
      {{ formatPlacementName(selectedPlacementId) }}
    </h2>
    <!-- delete/save buttons -->
    <div class="d-flex justify-space-between justify-sm-end">
      <v-btn color="error" @click="showDeleteDialog = true" class="ml-sm-2">
        <v-icon>mdi-delete</v-icon>
        <span class="d-none d-sm-block">Delete</span>
      </v-btn>
      <v-btn
        color="primary"
        @click="savePlacementSettings"
        class="ml-sm-2"
        :disabled="!hasChanges"
      >
        <v-icon>mdi-content-save</v-icon>
        <span class="d-none d-sm-block">Save</span>
      </v-btn>
    </div>
  </div>
  <!-- copy embed button -->
  <v-btn
    class="cursor-pointer mb-2 mt-2"
    @click="copyToClipboard"
    variant="elevated"
    size="small"
  >
    <v-icon class="mr-md-2">mdi-content-copy</v-icon>
    <span class="d-none d-md-block">{{
      copyStatus ? copyStatus : 'Copy embed code'
    }}</span>
  </v-btn>
  <!-- Preview -->
  <placement-preview
    :placement-id="selectedPlacementId"
    :loading-filters="loadingFilters"
    v-if="settings.placements.placements[selectedPlacementId]"
  ></placement-preview>
  <!-- Tabs -->
  <v-tabs
    v-model="currentTab"
    color="primary"
    class="d-flex justify-space-around"
    :eager="true"
  >
    <v-tab value="filters">
      <v-icon start class="mr-sm-2">mdi-filter</v-icon>
      <span class="d-none d-md-block">Filters</span>
    </v-tab>
    <v-tab value="settings">
      <v-icon start class="mr-sm-2">mdi-cog</v-icon>
      <span class="d-none d-md-block">Settings</span>
    </v-tab>
    <v-tab value="styles">
      <v-icon start class="mr-sm-2">mdi-view-module</v-icon>
      <span class="d-none d-md-block">Styles</span>
    </v-tab>
  </v-tabs>
  <!-- Filters & Styles -->
  <v-window v-model="currentTab" :touch="false" style="min-height: 500px">
    <!-- Placement dashboard -->
    <v-window-item value="filters">
      <v-sheet class="pa-4">
        <Filters
          :selected-placement="selectedPlacementId"
          @loading-filters="value => (loadingFilters = value)"
        />
      </v-sheet>
    </v-window-item>
    <v-window-item value="settings">
      <v-sheet class="pa-4">
        <BaseUrl :selected-placement="selectedPlacementId" />
        <ToggleCountOfMakeModels :selected-placement="selectedPlacementId" />
        <ToggleVehicleNameType :selected-placement="selectedPlacementId" />
        <v-card class="mb-4">
          <v-card-title>Sort Vehicles by Stock Count</v-card-title>
          <v-card-text v-if="selectedPlacement">
            <v-select
              v-model="selectedPlacement.sort"
              :items="[
                { title: 'Descending', value: 'desc' },
                { title: 'Ascending', value: 'asc' },
              ]"
              item-title="title"
              item-value="value"
              @update:modelValue="selectedPlacement.usedDrag = false"
              label="Sort"
            ></v-select>
          </v-card-text>
        </v-card>
        <ViewMode :selected-placement="selectedPlacementId" />
      </v-sheet>
    </v-window-item>
    <v-window-item value="styles">
      <PlacementSettingsForm
        :selected-placement-id="selectedPlacementId"
        @go-back="$emit('go-back')"
      ></PlacementSettingsForm>
    </v-window-item>
  </v-window>
  <!-- Delete Placement Dialog -->
  <v-dialog v-model="showDeleteDialog" max-width="500px">
    <v-card>
      <v-card-title>Delete Placement</v-card-title>
      <v-card-text>
        Are you sure you want to delete the placement "{{
          selectedPlacementId
        }}"? This action cannot be undone.
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="showDeleteDialog = false">
          Cancel
        </v-btn>
        <v-btn color="error" @click="deletePlacement"> Delete Placement </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import type { ISettings } from '@/types'
import { ref, onMounted, computed, watch } from 'vue'
import { useSettingsStore } from '@/stores/settings'
import PlacementSettingsForm from './PlacementSettingsForm.vue'
import Filters from './Filters.vue'
import PlacementPreview from './PlacementPreview.vue'
import ToggleCountOfMakeModels from './ToggleCountOfMakeModels.vue'
import ToggleVehicleNameType from './ToggleVehicleNameType.vue'
import BaseUrl from './BaseUrl.vue'
import ViewMode from './ViewMode.vue'

const emit = defineEmits(['go-back', 'update-success', 'update-error'])

const props = defineProps<{
  selectedPlacementId: string
}>()

const currentTab = ref('filters')

const settings = useSettingsStore()

const selectedPlacement = ref<ISettings | null>(null)
const originalPlacement = ref<ISettings | null>(null)

const loadingFilters = ref(true)

const showDeleteDialog = ref(false)

onMounted(() => {
  selectedPlacement.value =
    settings.placements.placements[props.selectedPlacementId] ?? []
  originalPlacement.value = JSON.parse(JSON.stringify(selectedPlacement.value))
})

const copyStatus = ref('')
const copyToClipboard = async () => {
  try {
    const content = `<script src='${import.meta.env.VITE_CDN}/jellybeans/cx/loader.js'><\/script><div class='jellybeans' data-account-id='${settings.accountId}' data-placement-id='${props.selectedPlacementId}'></div>`
    await navigator.clipboard.writeText(content)
    copyStatus.value = 'Copied to clipboard!'

    // Reset the icon and status after 2 seconds
    setTimeout(() => {
      copyStatus.value = ''
    }, 2000)
  } catch (err) {
    copyStatus.value = 'Failed to copy text'
    console.error('Failed to copy text:', err)
  }
}

const deletePlacement = async () => {
  delete settings.placements.placements[props.selectedPlacementId]
  await settings
    .updateSettings(settings.placements)
    .then(() => {
      emit('update-success')
      emit('go-back')
    })
    .catch((err: any) => {
      emit('update-error')
    })
}

const hasChanges = computed(() => {
  return (
    JSON.stringify(selectedPlacement.value) !==
    JSON.stringify(originalPlacement.value)
  )
})

const savePlacementSettings = async () => {
  if (selectedPlacement.value === null) return
  await settings
    .updateSettings(settings.placements)
    .then(() => {
      emit('update-success')
    })
    .catch((err: any) => {
      emit('update-error')
    })
}

const handleBack = () => {
  emit('go-back')
}

const formatPlacementName = (placementId: string | null) => {
  return (placementId ?? '').replace(/_/g, ' ').replace(/-/g, ' ').toUpperCase()
}
</script>

<style>
html {
  scroll-behavior: auto;
}
</style>
